import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useChallengeStore from '../hooks/useChallenge'
import { CheckCircle, Trophy, BarChart3, Clock, Target } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { createLogger } from '../utils/logger'

const logger = createLogger('ChallengeComplete')

const ChallengeComplete = () => {
  const navigate = useNavigate()
  const { currentChallenge, getChallengeStats, formatTime } = useChallengeStore()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadStats = async () => {
      if (currentChallenge) {
        try {
          const result = await getChallengeStats(currentChallenge.id)
          if (result.success) {
            setStats(result.stats)
          }
        } catch (error) {
          logger.error('Error loading challenge stats:', error)
        }
      }
      setLoading(false)
    }

    loadStats()
  }, [currentChallenge, getChallengeStats])

  const handleBackToDashboard = () => {
    navigate('/dashboard')
  }

  const handleStartNewChallenge = () => {
    navigate('/challenge-type')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <CheckCircle className="w-24 h-24 text-green-500" />
              <Trophy className="w-12 h-12 text-yellow-500 absolute -top-2 -right-2" />
            </div>
          </div>

          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Challenge Complete!
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
            Congratulations on completing the {currentChallenge?.name}
          </p>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
              <div className="flex justify-center mb-4">
                <BarChart3 className="w-8 h-8 text-blue-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {stats.totalAnswered || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Questions Answered</div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
              <div className="flex justify-center mb-4">
                <Target className="w-8 h-8 text-green-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {stats.correctAnswers || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Correct Answers</div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
              <div className="flex justify-center mb-4">
                <Clock className="w-8 h-8 text-purple-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {formatTime(stats.totalTimeSpent || 0)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Time</div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
              <div className="flex justify-center mb-4">
                <BarChart3 className="w-8 h-8 text-orange-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {stats.score ? `${stats.score}%` : 'N/A'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Overall Score</div>
            </div>
          </div>
        )}

        {/* Challenge Details */}
        {currentChallenge && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Challenge Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Challenge Information
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">Name:</span>
                    <span className="ml-2 text-gray-900 dark:text-gray-100">
                      {currentChallenge.name}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">Strategy:</span>
                    <span className="ml-2 text-gray-900 dark:text-gray-100">
                      {currentChallenge.strategyType}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">Status:</span>
                    <span className="ml-2 text-gray-900 dark:text-gray-100 capitalize">
                      {currentChallenge.status}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Stages Completed
                </h3>
                <div className="space-y-2">
                  {currentChallenge.stages?.map((stage, index) => (
                    <div key={stage.id} className="flex items-center space-x-3">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          stage.status === 'completed'
                            ? 'bg-green-500'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      />
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        Stage {stage.stageOrder}: {stage.name}
                      </span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          stage.status === 'completed'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                        }`}
                      >
                        {stage.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleBackToDashboard}
            className="px-6 py-3 text-lg font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Back to Dashboard
          </button>

          <button
            onClick={handleStartNewChallenge}
            className="px-6 py-3 text-lg font-medium text-white bg-primary-600 dark:bg-primary-700 rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
          >
            Start New Challenge
          </button>
        </div>
      </div>
    </div>
  )
}

export default ChallengeComplete
